import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IL_COLORS } from "../../../../constants/investmentLeague";

const COLS = [
  { key: "rank",   label: "Rank",        align: "left"   },
  { key: "unit",   label: "Investor",        align: "left"   },
  { key: "green",  label: "Green Picks", align: "left"   },
  { key: "roi",    label: "ROI",         align: "right"  },
  { key: "status", label: "Status",      align: "center" },
];

const cellSx = (align) => ({
  textAlign:  align,
  py:         1.5,
  px:         { xs: 1.5, md: 2 },
  fontSize:   "12.5px",
  whiteSpace: "nowrap",
});

const GreenProgressBar = ({ green, total, t }) => {
  const pct    = total > 0 ? (green / total) * 100 : 0;
  const isFull = green === total && total > 0;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: "140px" }}>
      <Typography sx={{ fontSize: "12px", fontWeight: 600, color: t.text, minWidth: "36px" }}>
        {green}/{total}
      </Typography>
      <Box
        sx={{
          flex:         1,
          height:       6,
          borderRadius: "3px",
          background:   t.surface3,
          overflow:     "hidden",
          minWidth:     "80px",
        }}
      >
        <Box
          sx={{
            width:        `${pct}%`,
            height:       "100%",
            borderRadius: "3px",
            background:   isFull ? IL_COLORS.positive : t.accent,
            transition:   "width 0.3s ease",
          }}
        />
      </Box>
    </Box>
  );
};

const LeaderboardTable = ({ rows = [] }) => {
  const theme = useTheme();
  const t     = theme.custom;

  if (!rows.length) {
    return (
      <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
        <Typography sx={{ fontSize: "13px", color: t.text3 }}>No leaderboard data available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        component="table"
        sx={{
          width:          "100%",
          minWidth:       "680px",
          borderCollapse: "collapse",
        }}
      >
        <Box component="thead">
          <Box component="tr" sx={{ background: t.surface2 }}>
            {COLS.map((col) => (
              <Box
                component="th"
                key={col.key}
                sx={{
                  ...cellSx(col.align),
                  fontSize:      "10px",
                  fontWeight:    600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color:         t.text3,
                  borderBottom:  `1px solid ${t.border}`,
                }}
              >
                {col.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Box component="tbody">
          {rows.map((row) => {
            const roiColor = row.roi >= 0 ? IL_COLORS.positive : IL_COLORS.negative;
            const rowBg    = row.isYou ? "rgba(40,179,180,0.10)" : "transparent";

            return (
              <Box
                component="tr"
                key={row.rank}
                sx={{
                  background:   rowBg,
                  borderBottom: `1px solid ${t.border}`,
                  "&:hover":    { background: row.isYou ? rowBg : t.surface2 },
                }}
              >
                <Box
                  component="td"
                  sx={{ ...cellSx("left"), fontWeight: 600, color: t.text }}
                >
                  {row.rank}
                </Box>

                <Box component="td" sx={cellSx("left")}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: row.isYou ? 600 : 400, color: t.text }}>
                      Investor - {row.unit}
                    </Typography>
                    {row.isYou && (
                      <Box
                        sx={{
                          fontSize:      "9px",
                          fontWeight:    700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color:         t.accent,
                          background:    `${t.accent}22`,
                          border:        `1px solid ${t.accent}44`,
                          borderRadius:  "3px",
                          px:            0.75,
                          py:            0.25,
                        }}
                      >
                        You
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box component="td" sx={cellSx("left")}>
                  <GreenProgressBar green={row.green} total={row.total} t={t} />
                </Box>

                <Box
                  component="td"
                  sx={{ ...cellSx("right"), fontWeight: 600, color: roiColor }}
                >
                  {row.roi >= 0 ? "+" : ""}{row.roi.toFixed(2)}%
                </Box>

                <Box component="td" sx={cellSx("center")}>
                  {row.status === "eligible" ? (
                    <Box
                      sx={{
                        display:       "inline-block",
                        fontSize:      "10px",
                        fontWeight:    700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color:         IL_COLORS.positive,
                        background:    IL_COLORS.positiveBg,
                        border:        `1px solid ${IL_COLORS.positive}44`,
                        borderRadius:  "12px",
                        px:            1.5,
                        py:            0.5,
                      }}
                    >
                      Eligible
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: "12px", color: t.text3 }}>—</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default LeaderboardTable;
